import { useCallback, useEffect, useRef, useState } from 'react';
import {
    projectsApi,
    accountsApi,
    suppliersApi,
    activitiesApi,
} from '../services/api';
import { useDebouncedSearchInput } from './useDebouncedSearchInput';

const PAGE_SIZE = 10;

/**
 * Loads and paginates the reference data needed by the shared TransactionForm
 * (projects, accounts, suppliers, activities), including debounced server-side
 * search for suppliers/activities and merging of the currently-selected
 * supplier/activity so it stays visible across searches.
 *
 * Consolidates the duplicated logic that previously lived in AddTransactionPage
 * and EditTransactionPage so it can be reused by the transaction modals.
 *
 * @param {object} options
 * @param {boolean} options.active   When false, no data is fetched (modal closed).
 * @param {object|null} options.transaction  Existing transaction (edit mode) used
 *   to seed the selected supplier/activity so the autocomplete shows its value.
 * @returns {{ loading: boolean, projects: any[], accounts: any[], formProps: object, resetInputs: () => void }}
 */
export function useTransactionFormData({ active = true, transaction = null } = {}) {
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [activities, setActivities] = useState([]);

    const [suppliersHasMore, setSuppliersHasMore] = useState(true);
    const [suppliersLoadingMore, setSuppliersLoadingMore] = useState(false);
    const [suppliersRefreshing, setSuppliersRefreshing] = useState(false);
    const nextSupplierPageRef = useRef(2);

    const [activitiesHasMore, setActivitiesHasMore] = useState(true);
    const [activitiesLoadingMore, setActivitiesLoadingMore] = useState(false);
    const [activitiesRefreshing, setActivitiesRefreshing] = useState(false);
    const nextActivityPageRef = useRef(2);

    const [supplierInput, setSupplierInput] = useState('');
    const [activityInput, setActivityInput] = useState('');
    const supplierSearchDebounced = useDebouncedSearchInput(supplierInput, 1200);
    const activitySearchDebounced = useDebouncedSearchInput(activityInput, 1200);

    const selectedSupplierIdRef = useRef(null);
    const selectedActivityIdsRef = useRef([]);
    const supplierSeqRef = useRef(0);
    const activitySeqRef = useRef(0);

    // Seed the selected supplier/activity from the transaction being edited so
    // the refresh effects below can merge it into the visible options.
    useEffect(() => {
        if (!transaction) return;
        const sid =
            typeof transaction.supplier === 'object' && transaction.supplier
                ? transaction.supplier.id
                : transaction.supplier;
        selectedSupplierIdRef.current = sid ?? null;
        // Activity lives per allocation; keep every selected id in the dropdown.
        const allocations = Array.isArray(transaction.project_transactions)
            ? transaction.project_transactions
            : [];
        selectedActivityIdsRef.current = allocations
            .map((pt) => (typeof pt.activity === 'object' && pt.activity ? pt.activity.id : pt.activity))
            .filter((value) => value != null && value !== '');
    }, [transaction]);

    useEffect(() => {
        if (!active) return undefined;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const [projectsData, accountsData] = await Promise.all([
                    projectsApi.getAll(),
                    accountsApi.getAll(),
                ]);
                if (cancelled) return;
                setProjects(projectsData);
                setAccounts(accountsData);
            } catch (e) {
                console.error('Failed to load transaction form data:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [active]);

    useEffect(() => {
        if (!active) return undefined;
        const seq = ++supplierSeqRef.current;
        let cancelled = false;
        setSuppliersRefreshing(true);
        nextSupplierPageRef.current = 2;
        setSuppliersHasMore(true);
        (async () => {
            try {
                const data = await suppliersApi.list({
                    page: 1,
                    page_size: PAGE_SIZE,
                    search: supplierSearchDebounced || undefined,
                });
                if (cancelled || seq !== supplierSeqRef.current) return;
                let merged = data.results;
                const sid = selectedSupplierIdRef.current;
                if (sid != null && sid !== '' && !merged.some((s) => String(s.id) === String(sid))) {
                    try {
                        const extra = await suppliersApi.getById(sid);
                        if (seq !== supplierSeqRef.current) return;
                        if (extra) {
                            merged = [...merged, extra].sort((a, b) =>
                                (a.name || '').localeCompare(b.name || '')
                            );
                        }
                    } catch (e) {
                        console.error('Failed to merge selected supplier into list:', e);
                    }
                }
                if (seq !== supplierSeqRef.current) return;
                setSuppliers(merged);
                setSuppliersHasMore(!!data.next);
            } catch (e) {
                console.error('Failed to load suppliers:', e);
            } finally {
                if (!cancelled && seq === supplierSeqRef.current) {
                    setSuppliersRefreshing(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [active, supplierSearchDebounced, transaction]);

    useEffect(() => {
        if (!active) return undefined;
        const seq = ++activitySeqRef.current;
        let cancelled = false;
        setActivitiesRefreshing(true);
        nextActivityPageRef.current = 2;
        setActivitiesHasMore(true);
        (async () => {
            try {
                const data = await activitiesApi.list({
                    page: 1,
                    page_size: PAGE_SIZE,
                    search: activitySearchDebounced || undefined,
                });
                if (cancelled || seq !== activitySeqRef.current) return;
                let merged = Array.isArray(data.results) ? [...data.results] : [];
                const selectedIds = selectedActivityIdsRef.current || [];
                const missing = selectedIds.filter(
                    (aid) => !merged.some((a) => String(a.id) === String(aid)),
                );
                if (missing.length) {
                    const extras = await Promise.all(missing.map(async (aid) => {
                        try {
                            return await activitiesApi.getById(aid);
                        } catch (e) {
                            console.error('Failed to merge selected activity into list:', e);
                            return null;
                        }
                    }));
                    if (seq !== activitySeqRef.current) return;
                    extras.filter(Boolean).forEach((extra) => {
                        if (!merged.some((a) => String(a.id) === String(extra.id))) {
                            merged.push(extra);
                        }
                    });
                    merged.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                }
                if (seq !== activitySeqRef.current) return;
                setActivities(merged);
                setActivitiesHasMore(!!data.next);
            } catch (e) {
                console.error('Failed to load activities:', e);
            } finally {
                if (!cancelled && seq === activitySeqRef.current) {
                    setActivitiesRefreshing(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [active, activitySearchDebounced, transaction]);

    const fetchMoreSuppliers = useCallback(async () => {
        if (!suppliersHasMore || suppliersLoadingMore || suppliersRefreshing) return;
        setSuppliersLoadingMore(true);
        try {
            const page = nextSupplierPageRef.current;
            const data = await suppliersApi.list({
                page,
                page_size: PAGE_SIZE,
                search: supplierSearchDebounced || undefined,
            });
            setSuppliers((prev) => [...prev, ...data.results]);
            setSuppliersHasMore(!!data.next);
            nextSupplierPageRef.current = page + 1;
        } catch (e) {
            console.error('Failed to load more suppliers:', e);
        } finally {
            setSuppliersLoadingMore(false);
        }
    }, [suppliersHasMore, suppliersLoadingMore, suppliersRefreshing, supplierSearchDebounced]);

    const fetchMoreActivities = useCallback(async () => {
        if (!activitiesHasMore || activitiesLoadingMore || activitiesRefreshing) return;
        setActivitiesLoadingMore(true);
        try {
            const page = nextActivityPageRef.current;
            const data = await activitiesApi.list({
                page,
                page_size: PAGE_SIZE,
                search: activitySearchDebounced || undefined,
            });
            setActivities((prev) => {
                const seen = new Set(prev.map((a) => String(a.id)));
                return [...prev, ...(data.results || []).filter((a) => !seen.has(String(a.id)))];
            });
            setActivitiesHasMore(!!data.next);
            nextActivityPageRef.current = page + 1;
        } catch (e) {
            console.error('Failed to load more activities:', e);
        } finally {
            setActivitiesLoadingMore(false);
        }
    }, [activitiesHasMore, activitiesLoadingMore, activitiesRefreshing, activitySearchDebounced]);

    const handleSupplierListScroll = useCallback(
        (event) => {
            const el = event.target;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
                fetchMoreSuppliers();
            }
        },
        [fetchMoreSuppliers]
    );

    const handleActivityListScroll = useCallback(
        (event) => {
            const el = event.target;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
                fetchMoreActivities();
            }
        },
        [fetchMoreActivities]
    );

    const resetInputs = useCallback(() => {
        setSupplierInput('');
        setActivityInput('');
    }, []);

    return {
        loading,
        projects,
        accounts,
        formProps: {
            projects,
            accounts,
            suppliers,
            activities,
            onSupplierListScroll: handleSupplierListScroll,
            suppliersLoadingMore: suppliersRefreshing || suppliersLoadingMore,
            onSupplierInputChange: setSupplierInput,
            onSupplierSelectedIdChange: (id) => {
                selectedSupplierIdRef.current = id;
            },
            onActivityListScroll: handleActivityListScroll,
            activitiesLoadingMore: activitiesRefreshing || activitiesLoadingMore,
            onActivityInputChange: setActivityInput,
            onActivitySelectedIdChange: (id) => {
                if (id == null || id === '') {
                    return;
                }
                if (!selectedActivityIdsRef.current.some((aid) => String(aid) === String(id))) {
                    selectedActivityIdsRef.current = [...selectedActivityIdsRef.current, id];
                }
            },
        },
        resetInputs,
    };
}

export default useTransactionFormData;
